import React, { useState } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  Layers,
  MessageSquareText,
  PlayCircle,
  Sparkles,
} from 'lucide-react';
import forestImage from '../assets/images/level_forest.png';
import code1Image from '../assets/images/how-it-works/algofox_code1.jpg';
import code2Image from '../assets/images/how-it-works/algofox_code2.jpg';
import code3Image from '../assets/images/how-it-works/algofox_code3.jpg';
import code4Image from '../assets/images/how-it-works/algofox_code4.jpg';
import code5Image from '../assets/images/how-it-works/algofox_code5.jpg';
import plan1Image from '../assets/images/how-it-works/algofox_plan1.jpg';
import plan2Image from '../assets/images/how-it-works/algofox_plan2.jpg';
import review1Image from '../assets/images/how-it-works/algofox_review1.jpg';
import test1Image from '../assets/images/how-it-works/algofox_test1.jpg';
import FoxMark from './FoxMark';

const mailchimpFormAction = process.env.REACT_APP_MAILCHIMP_FORM_ACTION || '';
const mailchimpBotFieldName = process.env.REACT_APP_MAILCHIMP_BOT_FIELD || '';

const trails = [
  {
    title: 'Track What Matters',
    question: 'What state do I need to carry?',
    examples: 'arrays, strings, hash maps, sets',
  },
  {
    title: 'Shrink The Search',
    question: 'Can structure let me skip work?',
    examples: 'sliding window, binary search',
  },
  {
    title: 'Explore A Space',
    question: 'What are the states and neighbors?',
    examples: 'trees, BFS, DFS, graphs',
  },
  {
    title: 'Make Choices',
    question: 'What decision comes next?',
    examples: 'recursion, backtracking, DP foundations',
  },
  {
    title: 'Prioritize Work',
    question: 'Which candidate should I process next?',
    examples: 'heaps, top-k, merge streams',
  },
];

const pricing = [
  {
    name: 'Starter',
    price: '$19/mo',
    description: 'Estimated launch price for 15 coached sessions per month, enough for steady weekly practice.',
    cta: 'Join launch list',
    featured: false,
  },
  {
    name: 'Focus',
    price: '$39/mo',
    description: 'Estimated launch price for 40 coached sessions per month for candidates actively preparing.',
    cta: 'Get launch updates',
    featured: true,
  },
  {
    name: 'Larger package',
    price: 'Get in touch',
    description: 'Need more sessions, team access, or a beta cohort for a study group? Tell us what you need.',
    cta: 'Contact at launch',
    featured: false,
  },
];

const walkthroughScreens = [
  {
    id: 'plan1',
    step: 'Plan',
    title: 'Write the approach first',
    image: plan1Image,
    alt: 'AlgoFox planning screen with a BFS approach typed into the planning box.',
  },
  {
    id: 'plan2',
    step: 'Plan check',
    title: 'Foxy checks the plan',
    image: plan2Image,
    alt: 'AlgoFox planning screen showing AI feedback on the candidate plan.',
  },
  {
    id: 'code1',
    step: 'Code',
    title: 'Ask while coding',
    image: code1Image,
    alt: 'AlgoFox coding screen with code in the editor and a question typed for Foxy.',
  },
  {
    id: 'code2',
    step: 'Coach',
    title: 'Get targeted feedback',
    image: code2Image,
    alt: 'AlgoFox coding screen showing Foxy responding to a code question.',
  },
  {
    id: 'code3',
    step: 'Code',
    title: 'Keep building',
    image: code3Image,
    alt: 'AlgoFox coding screen with more BFS code and another question for Foxy.',
  },
  {
    id: 'code4',
    step: 'Coach',
    title: 'Iterate with Foxy',
    image: code4Image,
    alt: 'AlgoFox coding screen showing another Foxy coaching response.',
  },
  {
    id: 'code5',
    step: 'Coach',
    title: 'Sanity-check the solution',
    image: code5Image,
    alt: 'AlgoFox coding screen showing final coaching feedback before tests.',
  },
  {
    id: 'test1',
    step: 'Tests',
    title: 'Run tests',
    image: test1Image,
    alt: 'AlgoFox coding screen showing passing test results.',
  },
  {
    id: 'review1',
    step: 'Review',
    title: 'Finish with a review',
    image: review1Image,
    alt: 'AlgoFox review screen with feedback categories and ratings.',
  },
];

const walkthroughSteps = [
  {
    id: 'plan1',
    eyebrow: 'Step 1',
    title: 'Explain your plan',
    copy: 'Write the approach, complexity, and edge cases you would explain in an interview.',
  },
  {
    id: 'plan2',
    eyebrow: 'Step 2',
    title: 'Get a plan check',
    copy: 'Foxy catches gaps early without handing you the solution.',
  },
  {
    id: 'code2',
    eyebrow: 'Step 3',
    title: 'Code with Foxy nearby',
    copy: 'Ask focused questions while you build, just like you would with an interviewer.',
  },
  {
    id: 'test1',
    eyebrow: 'Step 4',
    title: 'Run tests',
    copy: 'Use tests as evidence, then decide what still needs debugging or review.',
  },
  {
    id: 'review1',
    eyebrow: 'Step 5',
    title: 'Review the attempt',
    copy: 'Foxy turns your code, tests, and questions into specific feedback for the next rep.',
  },
];

const AlgoFoxLandingNew: React.FC = () => {
  const [email, setEmail] = useState('');
  const [signupState, setSignupState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [signupMessage, setSignupMessage] = useState('');
  const [activeWalkthroughId, setActiveWalkthroughId] = useState(walkthroughSteps[0].id);

  const activeWalkthrough = walkthroughScreens.find((screen) => screen.id === activeWalkthroughId) || walkthroughScreens[0];

  const handleSignupSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    setSignupState('submitting');
    setSignupMessage('');

    if (!mailchimpFormAction) {
      event.preventDefault();
      setSignupState('error');
      setSignupMessage('The launch list is not configured yet. Please try again soon.');
      return;
    }

    window.setTimeout(() => {
      setSignupState('success');
      setSignupMessage("You're on the launch list. We'll keep you posted.");
      setEmail('');
    }, 700);
  };

  return (
    <main className="min-h-screen bg-[#fbf8ef] text-[#17251d]">
      <nav className="sticky top-0 z-50 border-b border-[#2f4a37] bg-[#102319]/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-8">
          <a href="/" className="flex items-center gap-3 text-lg font-semibold text-[#fffdf7]">
            <FoxMark size="md" />
            AlgoFox
          </a>
          <div className="flex items-center gap-3">
            <a href="/about" className="hidden text-sm font-semibold text-[#dfe7d8] hover:text-white sm:inline">
              About
            </a>
            <a
              href="#launch-list"
              className="inline-flex h-10 items-center justify-center rounded-lg border border-[#dfe7d8]/35 px-4 text-sm font-semibold text-[#fffdf7] transition hover:bg-white/10"
            >
              Join launch list
            </a>
          </div>
        </div>
      </nav>

      <section className="relative isolate overflow-hidden border-b border-[#d8decf] bg-[#102319]">
        <img
          src={forestImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,35,25,0.98),rgba(16,35,25,0.88)_48%,rgba(216,111,49,0.24))]" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-16 lg:grid-cols-[1fr_0.95fr] lg:px-8 lg:py-20">
          <div className="max-w-3xl py-10">
            <div className="mb-7 inline-flex items-center gap-3 rounded-full border border-[#e0b46d]/40 bg-[#fffaf0]/10 px-4 py-2 text-sm font-semibold text-[#f0d7a6]">
              <Sparkles className="h-4 w-4" />
              Launching soon
            </div>
            <h1 className="text-6xl font-semibold leading-[0.98] tracking-normal text-[#fffdf7] md:text-7xl lg:text-8xl">
              AlgoFox
            </h1>
            <p className="mt-7 max-w-2xl text-xl leading-8 text-[#dfe7d8]">
              AI-native coding interview practice that teaches the mental models behind the algorithms, with Foxy as your AI coach from planning through postmortem.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-7 text-[#c7d4c0]">
              Practice with Foxy like an expert interviewer, without paying $100+ for every mock interview session.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href="#launch-list"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[#d86f31] px-5 text-base font-semibold text-white shadow-[0_16px_32px_rgba(216,111,49,0.28)] transition hover:bg-[#c65d27] focus:outline-none focus:ring-2 focus:ring-[#f0d7a6] focus:ring-offset-2 focus:ring-offset-[#102319]"
              >
                Join the launch list
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#demo"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-[#dfe7d8]/35 px-5 text-base font-semibold text-[#fffdf7] transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#f0d7a6] focus:ring-offset-2 focus:ring-offset-[#102319]"
              >
                See the flow
                <PlayCircle className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="rounded-lg border border-[#e9d7bc]/35 bg-[#fffaf0]/95 p-5 shadow-2xl shadow-black/25">
            <div className="flex items-start gap-4 border-b border-[#e2d7c2] pb-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#f7eadb]">
                <FoxMark size="md" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#c7652d]">Meet Foxy</p>
                <h2 className="mt-2 text-2xl font-semibold text-[#1e3527]">Your AI coding interview coach</h2>
                <p className="mt-3 text-sm leading-6 text-[#52614f]">
                  Foxy helps you practice the parts of interviews that problem lists miss: explaining your thinking, asking questions, making trade-offs, and reviewing the attempt after tests pass.
                </p>
              </div>
            </div>

            <div className="grid gap-3 py-5 sm:grid-cols-3">
              {[
                ['Plan', 'Explain approach and complexity before coding.'],
                ['Code', 'Ask Foxy questions while your solution takes shape.'],
                ['Review', 'Turn the session into concrete feedback.'],
              ].map(([label, copy]) => (
                <div key={label} className="rounded-lg border border-[#d8decf] bg-white p-3">
                  <CheckCircle2 className="h-4 w-4 text-[#31543f]" />
                  <p className="mt-3 text-sm font-semibold text-[#22382a]">{label}</p>
                  <p className="mt-1 text-xs leading-5 text-[#60705c]">{copy}</p>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-[#d8decf] bg-white p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#31543f]">
                <MessageSquareText className="h-4 w-4 text-[#d86f31]" />
                Chat like it is a real interview
              </div>
              <p className="mt-3 text-sm leading-6 text-[#52614f]">
                Clarifying questions and check-ins are not cheating here. They are part of the skill AlgoFox helps you build.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="demo" className="border-b border-[#e0e5d9] bg-[#fbf8ef] px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-end gap-8 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#c7652d]">How it works</p>
              <h2 className="mt-3 text-4xl font-semibold tracking-normal text-[#1f3428] md:text-5xl">
                See a full AI-coached question flow.
              </h2>
            </div>
            <p className="text-lg leading-8 text-[#5f6d59]">
              AlgoFox turns one interview problem into a coached session: explain the plan, code with Foxy beside you, run tests, and leave with a review you can act on.
            </p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-[390px_1fr]">
            <div className="space-y-3">
              {walkthroughSteps.map((step, index) => {
                const isActive = step.id === activeWalkthroughId;
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setActiveWalkthroughId(step.id)}
                    className={`w-full rounded-lg border p-4 text-left transition ${
                      isActive
                        ? 'border-[#d86f31] bg-[#fff8ef] shadow-sm shadow-[#d86f31]/10'
                        : 'border-[#d8decf] bg-white hover:border-[#c2cfb8]'
                    }`}
                  >
                    <div className="flex gap-4">
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                          isActive ? 'bg-[#d86f31] text-white' : 'bg-[#e9f0e2] text-[#31543f]'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span>
                        <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#c7652d]">
                          {step.eyebrow}
                        </span>
                        <span className="mt-1 block text-lg font-semibold text-[#1f3428]">{step.title}</span>
                        <span className="mt-2 block text-sm leading-6 text-[#60705c]">{step.copy}</span>
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="min-w-0">
              <div className="overflow-hidden rounded-lg border border-[#d8decf] bg-[#102319] shadow-xl shadow-[#2a3d2f]/15">
                <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#f0d7a6]">{activeWalkthrough.step}</p>
                    <h3 className="mt-1 text-lg font-semibold text-[#fffdf7]">{activeWalkthrough.title}</h3>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#d86f31]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#f0b35a]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#7da477]" />
                  </div>
                </div>
                <div className="bg-[#14251b] p-3">
                  <img
                    src={activeWalkthrough.image}
                    alt={activeWalkthrough.alt}
                    className="aspect-video w-full rounded-md object-cover"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="mt-4 overflow-x-auto pb-2">
                <div className="flex min-w-max gap-3">
                  {walkthroughScreens.map((screen) => {
                    const isActive = screen.id === activeWalkthroughId;
                    return (
                      <button
                        key={screen.id}
                        type="button"
                        onClick={() => setActiveWalkthroughId(screen.id)}
                        className={`w-36 shrink-0 overflow-hidden rounded-lg border bg-white text-left transition ${
                          isActive ? 'border-[#d86f31] ring-2 ring-[#f0c99e]' : 'border-[#d8decf] hover:border-[#c2cfb8]'
                        }`}
                      >
                        <img src={screen.image} alt="" className="aspect-video w-full object-cover" loading="lazy" />
                        <span className="block px-2.5 py-2 text-xs font-semibold text-[#31543f]">{screen.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="border-b border-[#e0e5d9] bg-[#f3efe4] px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#c7652d]">How it is different</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-normal text-[#1f3428] md:text-5xl">
              Practice the whole interview loop, not just isolated problems.
            </h2>
            <p className="mt-5 text-lg leading-8 text-[#5f6d59]">
              AlgoFox combines AI coaching with structured learning trails. Foxy helps you plan, code, debug, and review each attempt, while trails keep practice focused on transferable mental models instead of a giant topic list.
            </p>
          </div>

          <div className="mt-10 rounded-lg border border-[#d8decf] bg-[#fffdf7] p-5 shadow-sm">
            <div className="grid gap-4 md:grid-cols-3">
              {[
                ['AI coach', 'Foxy gives feedback in the flow of planning, coding, tests, and review.'],
                ['Structured path', 'Trails keep sessions focused without forcing you into a textbook table of contents.'],
                ['Transferable reps', 'Each problem is practice for a reusable way of thinking, not just one answer.'],
              ].map(([label, copy]) => (
                <div key={label} className="rounded-lg bg-[#f6f3ea] p-4">
                  <p className="text-sm font-semibold text-[#1f3428]">{label}</p>
                  <p className="mt-2 text-sm leading-6 text-[#60705c]">{copy}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#7a8673]">The structure behind the coaching</p>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            {trails.map((trail) => (
              <article key={trail.title} className="rounded-lg border border-[#d8decf] bg-[#fffdf7] p-5 shadow-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#e9f0e2] text-[#2d6043]">
                  <Layers className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-[#203629]">{trail.title}</h3>
                <p className="mt-3 text-sm font-semibold leading-6 text-[#31543f]">{trail.question}</p>
                <p className="mt-2 text-sm leading-6 text-[#60705c]">{trail.examples}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-[#e0e5d9] bg-[#f3efe4] px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#c7652d]">Estimated costs</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-normal text-[#1f3428] md:text-5xl">
              Built for serious solo practice.
            </h2>
            <p className="mt-5 text-lg leading-8 text-[#5f6d59]">
              Pricing is not final. We are working out costs based on real token usage, so the current model is session-based: each plan includes a capped number of AI-coached sessions to keep usage predictable.
            </p>
          </div>
          <div className="mt-12 grid gap-4 lg:grid-cols-3">
            {pricing.map((plan) => (
              <article
                key={plan.name}
                className={`rounded-lg border p-6 shadow-sm ${
                  plan.featured
                    ? 'border-[#d86f31] bg-[#fff8ef] shadow-[#7a3a18]/10'
                    : 'border-[#d8decf] bg-[#fffdf7]'
                }`}
              >
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#c7652d]">{plan.name}</p>
                <p className="mt-4 text-4xl font-semibold text-[#1f3428]">{plan.price}</p>
                <p className="mt-4 min-h-[72px] text-sm leading-6 text-[#60705c]">{plan.description}</p>
                <a
                  href="#launch-list"
                  className={`mt-6 inline-flex h-11 w-full items-center justify-center rounded-lg text-sm font-semibold transition ${
                    plan.featured
                      ? 'bg-[#d86f31] text-white hover:bg-[#c65d27]'
                      : 'border border-[#cfd8c4] bg-white text-[#31543f] hover:border-[#31543f]'
                  }`}
                >
                  {plan.cta}
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="launch-list" className="bg-[#183222] px-6 py-20 text-[#fffdf7] lg:px-8">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1fr_0.85fr]">
          <div>
            <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-[#e0b46d]/40 bg-white/10 px-4 py-2 text-sm font-semibold text-[#f0d7a6]">
              <FoxMark size="sm" />
              Launch list
            </div>
            <h2 className="text-4xl font-semibold tracking-normal md:text-5xl">
              Stay tuned for the AlgoFox launch.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-[#dfe7d8]">
              Get product updates, early access notes, and practical writing about coding interview preparation.
            </p>
          </div>

          <form
            onSubmit={handleSignupSubmit}
            action={mailchimpFormAction || undefined}
            method="post"
            target="mailchimp-signup-frame"
            className="rounded-lg border border-[#e9d7bc]/35 bg-[#fffaf0] p-5 text-[#1f3428] shadow-2xl shadow-black/20"
          >
            <label htmlFor="landing-new-email" className="text-sm font-bold uppercase tracking-[0.22em] text-[#c7652d]">
              Email address
            </label>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <input
                id="landing-new-email"
                type="email"
                name="EMAIL"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                className="h-12 min-w-0 flex-1 rounded-lg border border-[#cfd8c4] bg-white px-4 text-base text-[#1f3428] outline-none transition placeholder:text-[#8b9684] focus:border-[#d86f31] focus:ring-2 focus:ring-[#f0c99e]"
              />
              {mailchimpBotFieldName && (
                <div aria-hidden="true" className="absolute -left-[5000px]">
                  <input name={mailchimpBotFieldName} tabIndex={-1} defaultValue="" />
                </div>
              )}
              <button
                type="submit"
                disabled={signupState === 'submitting'}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[#d86f31] px-5 font-semibold text-white transition hover:bg-[#c65d27] focus:outline-none focus:ring-2 focus:ring-[#f0c99e]"
              >
                {signupState === 'submitting' ? 'Joining...' : 'Join list'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            {signupMessage && (
              <div
                className={`mt-4 rounded-md border px-3 py-2 text-sm font-semibold ${
                  signupState === 'success'
                    ? 'border-[#b9d1ad] bg-[#eef7e9] text-[#31543f]'
                    : 'border-[#e5b68d] bg-[#fff8ef] text-[#9a481d]'
                }`}
              >
                {signupMessage}
              </div>
            )}
            <p className="mt-4 text-sm leading-6 text-[#64715d]">
              No spam. Just updates while the product takes shape. Read the{' '}
              <a href="/privacy" className="font-semibold text-[#31543f] underline decoration-[#d86f31]/40 underline-offset-4 hover:text-[#d86f31]">
                privacy policy
              </a>.
            </p>
            <iframe
              title="Mailchimp signup"
              name="mailchimp-signup-frame"
              className="hidden"
            />
          </form>
        </div>
        <div className="mx-auto mt-10 flex max-w-7xl justify-center gap-5 border-t border-white/10 pt-6">
          <a href="/about" className="text-sm font-semibold text-[#dfe7d8] hover:text-white">
            About
          </a>
          <a href="/privacy" className="text-sm font-semibold text-[#dfe7d8] hover:text-white">
            Privacy Policy
          </a>
        </div>
      </section>
    </main>
  );
};

export default AlgoFoxLandingNew;
